import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { SlideOver } from "@/components/SlideOver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { WAREHOUSE } from "@/lib/constants";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function ProductForm({ clients, types, product, onSaved }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const typeOptions = product && !types.includes(product.product_type) ? [product.product_type, ...types] : types;

  const today = new Date().toISOString().slice(0, 10);
  const empty = {
    serial_number: "", product_type: types[0] || "DSK", client_id: clients[0]?.id || "",
    warehouse_location: "", received_date: today, stick1_serial: "", stick2_serial: "", notes: "",
  };
  const [v, setV] = useState(product ? {
    serial_number: product.serial_number, product_type: product.product_type, client_id: product.client_id,
    warehouse_location: product.warehouse_location || "", received_date: product.received_date || "",
    stick1_serial: product.stick1_serial || "",
    stick2_serial: product.stick2_serial || "", notes: product.notes || "",
  } : empty);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/warehouses").then(({ data }) => {
      if (cancelled) return;
      setWarehouses(data || []);
      if (!product && data && data.length > 0 && !selectedWarehouseId) {
        const dolphin = data.find((w) => /dolphin/i.test(w.name)) || data[0];
        setSelectedWarehouseId(dolphin.id);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  function onWarehouseChange(wid) {
    setSelectedWarehouseId(wid);
  }

  function set(k, val) { setV((prev) => ({ ...prev, [k]: val })); }

  const receivedDateError = v.received_date && v.received_date > today
    ? "Received Date cannot be in the future. Please choose today or an earlier date."
    : "";

  async function submit(e) {
    e.preventDefault();
    if (receivedDateError) return;
    setError(null);
    setSaving(true);
    try {
      if (product) await api.put(`/products/${product.id}`, v);
      else await api.post("/products", v);
      setOpen(false);
      if (!product) { setV(empty); setSelectedWarehouseId(""); }
      onSaved ? onSaved() : navigate(0);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {product ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}><Pencil className="h-4 w-4" /> Edit</Button>
      ) : (
        <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add Product</Button>
      )}
      <SlideOver open={open} onClose={() => setOpen(false)} title={product ? `Edit ${product.serial_number}` : "Add Product"}>
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <div className="space-y-2">
            <Label htmlFor="serial_number">Serial Number *</Label>
            <Input id="serial_number" value={v.serial_number} onChange={(e) => set("serial_number", e.target.value)} placeholder="e.g. DSK-TM-0004" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="product_type">Product Type *</Label>
            {addingType ? (
              <div className="flex gap-2">
                <Input value={v.product_type} onChange={(e) => set("product_type", e.target.value)} placeholder="e.g. Kiosk" autoFocus required />
                <Button type="button" variant="outline" onClick={() => { setAddingType(false); set("product_type", typeOptions[0] || "DSK"); }}>Cancel</Button>
              </div>
            ) : (
              <select className={selectClass} value={v.product_type} onChange={(e) => { if (e.target.value === "__new__") { setAddingType(true); set("product_type", ""); } else set("product_type", e.target.value); }} required>
                {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="__new__">+ Add new type…</option>
              </select>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="client_id">Organization *</Label>
            <select id="client_id" className={selectClass} value={v.client_id} onChange={(e) => set("client_id", e.target.value)} required>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="warehouse_id">Warehouse</Label>
            <select id="warehouse_id" className={selectClass} value={selectedWarehouseId} onChange={(e) => onWarehouseChange(e.target.value)} data-testid="warehouse-select">
              <option value="">Select a warehouse…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}{w.city ? ` — ${w.city}${w.state ? `, ${w.state}` : ''}` : ''}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">Picking a warehouse auto-fills the location below — you can still add rack/shelf detail.</p>
          </div>
          <div className="space-y-2">
          <div className="space-y-2">
            <Label htmlFor="warehouse_location">Aisle</Label>
            <Input id="warehouse_location" value={v.warehouse_location} onChange={(e) => set("warehouse_location", e.target.value)} placeholder="e.g. Aisle 1, Rack A3 Shelf 2" data-testid="aisle-input" />
            <p className="text-xs text-muted-foreground">Stored at <span className="font-medium">{WAREHOUSE}</span></p>
          </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="received_date">Received at Warehouse</Label>
            <Input
              id="received_date"
              type="date"
              max={today}
              value={v.received_date}
              onChange={(e) => set("received_date", e.target.value)}
              className={receivedDateError ? "border-rose-400 focus-visible:ring-rose-400" : ""}
              data-testid="received-date-input"
            />
            {receivedDateError ? (
              <p className="text-xs font-medium text-rose-600" data-testid="received-date-error">{receivedDateError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">The date this product physically arrived at the Dolphin warehouse.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>T-00 Sticks (fixed to this DSK)</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input value={v.stick1_serial} onChange={(e) => set("stick1_serial", e.target.value)} placeholder="Stick 1 serial" />
              <Input value={v.stick2_serial} onChange={(e) => set("stick2_serial", e.target.value)} placeholder="Stick 2 serial" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea id="notes" rows={3} value={v.notes} onChange={(e) => set("notes", e.target.value)} className={textareaClass} />
          </div>
          <Button type="submit" className="w-full" disabled={saving || !!receivedDateError}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {product ? "Save Changes" : "Add Product"}
          </Button>
        </form>
      </SlideOver>
    </>
  );
}

import { PRODUCT_STATUSES } from "@/lib/constants";

export function ProductStatusForm({ productId, currentStatus, onSaved }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(PRODUCT_STATUSES.includes(currentStatus) ? currentStatus : "In Warehouse");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null); setSaving(true);
    try {
      await api.post(`/products/${productId}/status`, { status, note });
      setOpen(false); setNote("");
      onSaved && onSaved();
    } catch (err) {
      setError(apiError(err));
    } finally { setSaving(false); }
  }

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>Update Status</Button>
      <SlideOver open={open} onClose={() => setOpen(false)} title="Update Product Status">
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <p className="text-sm text-muted-foreground">Current status: <span className="font-medium">{currentStatus}</span></p>
          <div className="space-y-2">
            <Label htmlFor="status">New Status</Label>
            <select id="status" className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
              {PRODUCT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="note">Note (optional)</Label>
            <textarea id="note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={textareaClass} />
          </div>
          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Status
          </Button>
        </form>
      </SlideOver>
    </>
  );
}
